import React from 'react'
import styled from 'styled-components'
import { useQuery, UseQueryResult } from 'react-query'

import { Helmet } from 'react-helmet'
import { NavLink as RouterNavLink, NavLinkProps, useParams } from 'react-router-dom'
import {
  Breadcrumbs as MuiBreadcrumbs,
  Button as MuiButton,
  Card as MuiCard,
  CardContent,
  Divider as MuiDivider,
  Grid,
  Link,
  Paper,
  Typography,
} from '@material-ui/core'
import { spacing } from '@material-ui/system'
import { Add as AddIcon, LocationOn as LocationOnIcon } from '@material-ui/icons'

import { HierarchyPath } from 'src/components/Tree'
import {
  useQueryPaginator,
  DateTimeField,
  DetailTable,
  JSONField,
  LinkedField,
  PaginatedTable,
  ProfileField,
  RelatedLinkedField,
  ValueField,
} from 'src/components/Table'
import { ImageGallery } from 'src/components/ImageGallery'

import { skuit } from 'src/services/skuit'
import { BarcodedUnit } from 'src/api/models'

const NavLink = React.forwardRef((props: NavLinkProps, ref: React.Ref<HTMLAnchorElement>) => (
  <RouterNavLink innerRef={ref} {...props} />
))
NavLink.displayName = 'NavLink'

const Button = styled(MuiButton)(spacing)

const Card = styled(MuiCard)(spacing)

const Divider = styled(MuiDivider)(spacing)

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing)

const Centered = styled.div`
  text-align: center;
`

interface DetailsProps {
  unit: UseQueryResult<BarcodedUnit>
}

const Details = (props: DetailsProps) => {
  const { unit } = props
  return (
    <Card mb={6}>
      <CardContent>
        <Centered>
          <LocationOnIcon />
          <Typography variant="h6" gutterBottom>
            {unit && unit.data?.name}
          </Typography>
          <DetailTable
            obj={unit}
            fields={[
              { id: 'guid', label: 'SquID', renderer: ValueField },
              { id: 'name', label: 'Name', renderer: ValueField },
              {
                id: 'facility',
                label: 'Facility',
                renderer: RelatedLinkedField(ValueField, 'name'),
              },
              {
                id: 'location',
                label: 'Location',
                renderer: RelatedLinkedField(ValueField, 'name'),
              },
            ]}
          />
        </Centered>
      </CardContent>
    </Card>
  )
}

interface PathParams {
  guid: string
}

export const BarcodedUnitPage = () => {
  const { guid } = useParams<PathParams>()
  const unit = useQuery<BarcodedUnit>(['barcoded-unit'], () =>
    skuit.sc.barcodedUnits.retrieve(guid)
  )

  const freightShipmentsPaginator = useQueryPaginator(
    'freight-shipment-list',
    skuit.sc.freight.shipments.list,
    { params: { page_size: 5, statuses: 'pending', barcoded: guid } }
  )
  const parcelShipmentsPaginator = useQueryPaginator(
    'parcel-shipment-list',
    skuit.sc.parcel.shipments.list,
    { params: { page_size: 5, statuses: 'pending', barcoded: guid } }
  )
  const eventsPaginator = useQueryPaginator('event-list', skuit.sc.events.list, {
    params: { page_size: 5, references: guid },
  })

  return (
    <>
      <Helmet title="Unit (Barcoded)" />

      <Grid justify="space-between" container spacing={10}>
        <Grid item>
          <Typography variant="h3" gutterBottom display="inline">
            Unit (Barcoded)
          </Typography>

          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Link component={NavLink} exact to="/">
              Dashboard
            </Link>
            <Link component={NavLink} exact to="/inventory/barcoded-units">
              Units (Barcoded)
            </Link>
            <Typography>{unit && unit.data?.name}</Typography>
          </Breadcrumbs>
        </Grid>
        <Grid item>
          <div>
            <Button variant="contained" color="primary">
              <AddIcon />
              New Unit (Barcoded)
            </Button>
          </div>
        </Grid>
      </Grid>

      <Divider my={6} />

      <Grid container spacing={6}>
        <Grid item xs={12}>
          <Grid container spacing={6}>
            <Grid item xs={12} lg={6}>
              <Details unit={unit} />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Paper>
            <ImageGallery title="Product Images" images={unit && unit.data?.images} />
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <HierarchyPath title="Storage Path" location={unit} />
        </Grid>
        <Grid item xs={12}>
          <PaginatedTable
            title="Active Freight Shipments"
            fields={[
              { id: 'name', label: 'Name', renderer: LinkedField(ValueField) },
              { id: 'status', label: 'Status', renderer: ValueField },
            ]}
            paginator={freightShipmentsPaginator}
          />
        </Grid>
        <Grid item xs={12}>
          <PaginatedTable
            title="Active Parcel Shipments"
            fields={[
              { id: 'name', label: 'Name', renderer: LinkedField(ValueField) },
              { id: 'status', label: 'Status', renderer: ValueField },
            ]}
            paginator={parcelShipmentsPaginator}
          />
        </Grid>
        <Grid item xs={12}>
          <PaginatedTable
            title="Unit (Barcoded) Events"
            fields={[
              { id: 'type', label: 'Type', renderer: ValueField },
              { id: 'data', label: 'Data', renderer: JSONField },
              { id: 'by', label: 'By', renderer: ProfileField },
              { id: 'on', label: 'Occurred On', renderer: DateTimeField },
            ]}
            paginator={eventsPaginator}
          />
        </Grid>
      </Grid>
    </>
  )
}
